<template>
  <div class="w-full">
    <vx-card :title="portfolioId ? 'Edit your portfolio' : 'Create your own portfolio'">
      <div v-if="currentPortfolio">
        <validation-observer ref="validation-observer" v-slot="{ handleSubmit }">
          <!-- Warning message to say user cannot edit (if that's a predefined portfolio) -->
          <p v-if="isCurrentPortfolioPredefined" class="text-warning mb-8">
            This is a portfolio created by Alquant. You cannot modify nor delete this portfolio.
          </p>

          <!-- Name -->
          <validation-provider
            v-slot="{ errors }"
            rules="required|min_char:1|max_char:50"
            mode="eager"
            class="mb-8"
            tag="div"
          >
            <vs-input
              v-model="currentPortfolio.name"
              size="large"
              name="Portfolio name"
              :disabled="isCurrentPortfolioPredefined"
              class="w-full md:w-1/3"
            />
            <span class="text-danger text-sm">{{ errors[0] }}</span>
          </validation-provider>

          <div v-if="!isSmallScreen">
            <!-- Portfolio creation -->
            <div class="vx-row mb-base">
              <div class="vx-col w-full">
                <div class="vx-row mb-4">
                  <div class="vx-col w-3/4 md:w-2/3">Asset</div>
                  <div class="vx-col w-1/8 md:w-1/4">Target weight (in %)</div>
                  <div class="vx-col w-1/8 md:w-1/12"></div>
                </div>
                <div
                  v-for="(selected, index) in currentPortfolio.assets"
                  :key="'asset_' + index"
                  class="vx-row mb-8"
                >
                  <div class="vx-col w-3/4 md:w-2/3 mb-4 md:mb-0">
                    <validation-provider
                      v-slot="{ errors }"
                      :rules="{
                        required: true,
                        excluded_assets: {
                          excludedAssets: currentPortfolio.assets
                            .filter((asset) => asset?.asset)
                            .map((asset) => asset.asset),
                        },
                      }"
                      mode="eager"
                    >
                      <!-- It's important to set the :key as it allows the component to be reloaded.
                      Without it, changing portfolio doesn't change the element at index idx -->
                      <sp-global-input-search
                        :key="selected.asset ? selected.asset.ticker : index"
                        v-model="selected.asset"
                      ></sp-global-input-search>
                      <span class="text-danger text-sm">{{ errors[0] }}</span>
                    </validation-provider>
                  </div>
                  <div class="vx-col w-1/8 md:w-1/6">
                    <validation-provider
                      v-slot="{ errors }"
                      rules="required|max_value:100.0|min_value:0.0"
                      mode="eager"
                    >
                      <vs-input
                        v-model="selected.saa"
                        :name="'current_' + index"
                        class="w-full"
                        type="number"
                        @wheel="$event.target.blur()"
                      />
                      <span class="text-danger text-sm">{{ errors[0] }}</span>
                    </validation-provider>
                  </div>
                  <div class="vx-col w-1/8 md:w-1/6 flex justify-center">
                    <template>
                      <feather-icon
                        icon="MinusIcon"
                        class="m-0 cursor-pointer"
                        @click="removeAsset(index)"
                      />
                    </template>
                  </div>
                </div>
                <div class="vx-row">
                  <div class="vx-col w-3/4 md:w-2/3 mb-4 md:mb-0"></div>
                  <div class="vx-col w-1/8 md:w-1/6 flex justify-center">
                    <vs-button
                      v-tooltip="{
                        content: 'Click to equally weight your portfolio',
                        boundariesElement: 'window',
                      }"
                      size="small"
                      color="primary"
                      type="filled"
                      :disabled="isCurrentPortfolioPredefined"
                      @click="equallyWeightPortfolio"
                      @mouseenter="logTooltip('equally-weight')"
                      ><span class="text-base">=</span></vs-button
                    >
                  </div>
                  <div class="vx-col w-1/8 md:w-1/6"></div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div
              v-for="(selected, index) in currentPortfolio.assets"
              :key="'asset_' + index"
              class="mb-base mx-4"
            >
              <div class="vx-row my-2">
                <div class="vx-col w-4/5">
                  <div class="vx-row my-2">
                    <div class="w-full">
                      <validation-provider
                        v-slot="{ errors }"
                        :rules="{
                          required: true,
                          excluded_assets: {
                            excludedAssets: currentPortfolio.assets
                              .filter((asset) => asset?.asset)
                              .map((asset) => asset.asset),
                          },
                        }"
                        mode="eager"
                      >
                        <!-- It's important to set the :key as it allows the component to be reloaded.
                       Without it, changing portfolio doesn't change the element at index idx -->
                        <sp-global-input-search
                          :key="selected.asset ? selected.asset.ticker : index"
                          v-model="selected.asset"
                        ></sp-global-input-search>
                        <span class="text-danger text-sm">{{ errors[0] }}</span>
                      </validation-provider>
                    </div>
                  </div>
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required|max_value:100.0|min_value:0.0"
                    mode="eager"
                    tag="div"
                  >
                    <div class="vx-row my-2 flex justify-between items-center">
                      <div class="w-3/4">Target weight (in %)</div>
                      <div class="w-1/4">
                        <vs-input
                          v-model="selected.saa"
                          :name="'current_' + index"
                          class="w-full"
                          type="number"
                          @wheel="$event.target.blur()"
                        />
                      </div>
                      <span class="text-danger text-sm">{{ errors[0] }}</span>
                    </div>
                  </validation-provider>
                </div>
                <div class="vx-col w-1/5 flex justify-center items-center">
                  <feather-icon
                    icon="MinusIcon"
                    class="m-0 p-0 cursor-pointer"
                    @click="removeAsset(index)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Add an element button -->
          <div class="mb-base flex justify-center">
            <template>
              <feather-icon icon="PlusIcon" class="m-0 cursor-pointer" @click="addAsset" />
            </template>
          </div>

          <!-- Save and delete buttons -->
          <div class="vx-row justify-center">
            <vs-button
              size="large"
              class="vx-col col-12 md:col-6 mx-6 my-4"
              color="primary"
              type="filled"
              :disabled="isCurrentPortfolioPredefined"
              @click="handleSubmit(saveCurrentPortfolio)"
              >Save</vs-button
            >
            <vs-button
              size="large"
              class="vx-col col-12 md:col-6 mx-6 my-4"
              color="danger"
              type="filled"
              :disabled="isCurrentPortfolioPredefined"
              @click="askForPortfolioDeletion"
              >Delete</vs-button
            >
            <validation-provider
              v-slot="{ errors }"
              :rules="{
                sum: {
                  expectedValue: 100,
                  tolerance: 0.01,
                  values: currentPortfolio.assets
                    .filter((asset) => asset.saa)
                    .map((asset) => parseFloat(asset.saa)),
                },
              }"
              :skip-if-empty="false"
              tag="div"
              class="w-full flex justify-center mb-4"
              :debounce="1000"
            >
              <span class="text-danger text-sm">{{ errors[0] }}</span>
            </validation-provider>
          </div>
        </validation-observer>
      </div>
      <div v-else-if="allPortfolios === null" class="text-danger">
        {{ serverErrorMessage }}
      </div>
    </vx-card>
  </div>
</template>

<script>
import firebase from "firebase/compat/app";
import _ from "lodash";

import {
  BUTTON_CLICKED_EVENT,
  CREATE_PORTFOLIO_EVENT,
  DELETE_PORTFOLIO_EVENT,
  EDIT_PORTFOLIO_EVENT,
  STATUS_FAILURE,
  STATUS_SUCCESS,
  TOOLTIP_DISPLAYED_EVENT,
} from "@/assets/constants/analytics";
import { SERVER_ERROR_MESSAGE } from "@/assets/constants/global";
import SpGlobalInputSearch from "@/components/input/SpGlobalInputSearch.vue";

export default {
  name: "EditPortfolio",
  components: {
    SpGlobalInputSearch,
  },
  props: {
    portfolioId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      serverErrorMessage: SERVER_ERROR_MESSAGE,
      selectedPortfolio: null,
      currentPortfolio: null,
      validationErrors: [],
    };
  },
  computed: {
    isSmallScreen() {
      const currentBreakPoint = this.$store.getters["windowBreakPoint"];
      return currentBreakPoint == "sm" || currentBreakPoint == "xs";
    },
    allPortfolios() {
      return this.$store.getters["portfolios/getPortfolios"];
    },
    isCurrentPortfolioPredefined() {
      if (this.currentPortfolio == null) {
        return null;
      }

      return this.currentPortfolio.type === "predefined";
    },
  },
  watch: {
    allPortfolios: {
      handler: function (newPortfolios) {
        // Only execute this if the portfolios were bound (i.e. newPortfolios is
        // not null) and if we haven't set the currentPortfolio yet.
        if (this.currentPortfolio == null && newPortfolios != null) {
          // Check if this is not a new portfolio, and if that's not the case, get it from Firestore.
          if (this.portfolioId) {
            this.selectedPortfolio = newPortfolios.find(
              (portfolio) => portfolio.id === this.portfolioId
            );
            this.currentPortfolio = _.cloneDeep(this.selectedPortfolio);
          } else {
            this.currentPortfolio = this.generateNewPortfolio(newPortfolios);
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    generateNewPortfolio(allPortfolios) {
      // Iterate over all portfolios to see if we created an id called "portfolio1", ...
      let n = 1;
      let foundFreeId = false;
      let portfolioId;
      while (!foundFreeId) {
        portfolioId = `portfolio${n}`;
        if (!allPortfolios.some((portfolio) => portfolio.id === portfolioId)) {
          foundFreeId = true;
        }
        n++;
      }

      // Iterate over all portfolios to see if we can call it "Portfolio 1", ...
      n = 1;
      let foundFreeName = false;
      let portfolioName;
      while (!foundFreeName) {
        portfolioName = `Portfolio ${n}`;
        if (!allPortfolios.some((portfolio) => portfolio.name === portfolioName)) {
          foundFreeName = true;
        }
        n++;
      }

      return {
        id: portfolioId,
        name: portfolioName,
        type: "user-defined",
        assets: [
          {
            asset: null,
            risk: null,
            lower: "0.0",
            saa: null,
            upper: "100.0",
          },
        ],
      };
    },
    saveCurrentPortfolio() {
      // Set 'updated' timestamp, as well as 'created' if this is a new one.
      const currentTimestamp = firebase.firestore.FieldValue.serverTimestamp();
      const isNewPortfolio = this.selectedPortfolio == null;
      this.currentPortfolio.updated = currentTimestamp;

      // If the portfolio is new, set the current server timestamp as 'created' field.
      // It can happen that sometimes the 'created' and 'updated' fields are not yet updated
      // (it takes a few seconds for Firestore to execute the 'serverTimestamp()'), so when we
      // change 'selectedPortfolio', the 'created' and 'updated' fields are still null. It
      // will happen if someone created a portfolio, and updates it after, without leaving
      // the page. In those cases, we will just put a new 'created' time (we don't care that much
      // about this time, and it will still be approx. correct as this issue happens when we edit
      // a portfolio right after having created it).
      if (isNewPortfolio || this.selectedPortfolio.created == null) {
        this.currentPortfolio.created = currentTimestamp;
      } else {
        this.currentPortfolio.created = this.selectedPortfolio.created;
      }

      // Make sure all weights (SAA) are of type "number"; it's not the case when the user manually
      // puts number, and we don't want to use v-model.number because it has some weird behaviour:
      // https://github.com/vuejs/vue/issues/7136.
      this.currentPortfolio.assets = this.currentPortfolio.assets.map((asset) => {
        asset.saa = parseFloat(asset.saa);
        return asset;
      });

      // Update the portfolio in firestore.
      this.$store
        .dispatch("portfolios/updatePortfolio", {
          portfolioId: this.currentPortfolio.id,
          newPortfolio: this.currentPortfolio,
        })
        .then(() => {
          if (isNewPortfolio) {
            this.$analytics.logEvent(CREATE_PORTFOLIO_EVENT, {
              new_name: this.currentPortfolio.name,
              status: STATUS_SUCCESS,
            });
          } else {
            this.$analytics.logEvent(EDIT_PORTFOLIO_EVENT, {
              new_name: this.currentPortfolio.name,
              status: STATUS_SUCCESS,
            });
          }

          this.$vs.notify({
            title: "Saved",
            text: `Your portfolio was saved successfully.`,
            color: "success",
            time: 5000,
          });

          this.$emit("save-portfolio", this.currentPortfolio.id);
        })
        .catch((err) => {
          this.$analytics.logEvent(EDIT_PORTFOLIO_EVENT, {
            new_name: this.currentPortfolio.name,
            message: err.message || err,
            status: STATUS_FAILURE,
          });

          // We rethrow the error for sentry to catch it.
          throw err;
        });
    },
    addAsset() {
      this.$analytics.logEvent(BUTTON_CLICKED_EVENT, {
        content: "+",
      });
      this.currentPortfolio.assets.push({
        asset: null,
        risk: null,
        lower: "0.0",
        saa: null,
        upper: "100.0",
      });
    },
    equallyWeightPortfolio() {
      this.$analytics.logEvent(BUTTON_CLICKED_EVENT, {
        content: "=",
      });

      const equallyWeight = Math.floor((100 / this.currentPortfolio.assets.length) * 1000) / 1000;
      this.currentPortfolio.assets.forEach((asset) => {
        asset.saa = equallyWeight;
      });
      // In order to always sum to exactly 100, we put in the first SAA
      // the remainder that we might have if we split by a non-divisor of 100.
      this.currentPortfolio.assets[0].saa =
        Math.round((100 - equallyWeight * (this.currentPortfolio.assets.length - 1)) * 1000) / 1000;
    },
    removeAsset(index) {
      this.$analytics.logEvent(BUTTON_CLICKED_EVENT, {
        content: "-",
      });
      this.currentPortfolio.assets.splice(index, 1);
      // If we validate immediately, the lists used in the "excluded_*"
      // rules won't have time to update, and we want to potentially
      // clean few errors if one asset is removed.
      setTimeout(
        async () =>
          await this.$refs["validation-observer"].validate({
            silent: false,
          }),
        100
      );
    },
    askForPortfolioDeletion() {
      this.$vs.dialog({
        type: "confirm",
        color: "danger",
        title: "Confirm",
        text: "Deleting your portfolio will permanently delete it, with no way of getting it back.",
        accept: this.deleteSelectedPortfolio,
      });
    },
    deleteSelectedPortfolio() {
      // If we haven't saved the portfolio yet (i.e. the portfolio isn't
      // really in Firestore yet), just make the user think that it was correctly deleted.
      if (this.selectedPortfolio == null) {
        this.currentPortfolio = null;
        this.$analytics.logEvent(DELETE_PORTFOLIO_EVENT, {
          status: STATUS_SUCCESS,
          message: "Deleted successfully without even being in Firestore",
        });
        this.$vs.notify({
          title: "Deleted",
          text: `Your portfolio was successfully deleted.`,
          color: "success",
          time: 5000,
        });

        this.$emit("delete-portfolio");
        return;
      }

      // Delete the portfolio.
      const portfolioId = this.currentPortfolio.id;
      this.$store
        .dispatch("portfolios/deletePortfolio", {
          portfolioId: portfolioId,
        })
        .then(() => {
          this.$analytics.logEvent(DELETE_PORTFOLIO_EVENT, {
            status: STATUS_SUCCESS,
          });
          this.$vs.notify({
            title: "Deleted",
            text: `Your portfolio was successfully deleted.`,
            color: "success",
            time: 5000,
          });

          this.$emit("delete-portfolio");
        })
        .catch((err) => {
          this.$analytics.logEvent(DELETE_PORTFOLIO_EVENT, {
            message: err.message || err,
            status: STATUS_FAILURE,
          });
          this.$vs.notify({
            title: "Deleted",
            text: "Couldn't delete your portfolio successfully. Please contact us to resolve this issue.",
            color: "danger",
            time: 5000,
          });

          // We rethrow the error for sentry to catch it.
          throw err;
        });
    },
    logTooltip(name) {
      this.$analytics.logEvent(TOOLTIP_DISPLAYED_EVENT, {
        tooltip: `portfolio_${name}`,
      });
    },
  },
};
</script>
